/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * The commission tier of a contract — the one rule behind the "Laag / Gemiddeld /
 * Hoog" label and the colour it is printed in, on the result row and in the
 * contract bars of a provider profile (ContractMetrics).
 *
 * Moved out of ContractMetrics.tsx so a surface that only needs the rule does
 * not import a React component file to get it; ContractMetrics imports it from
 * here like every other surface (its re-export went on 2026-10-07, with the
 * cards comparer that was its last importer). The thresholds moved verbatim:
 * changing one changes every surface that shows a tier.
 *
 * The class maps hold literal strings on purpose: Tailwind's scanner reads
 * source text, and a class name assembled at runtime is never generated. They
 * are the classes ContractMetrics prints today (its own ladders stay as they
 * are); the result row reads these maps.
 *
 * Pure on purpose (the providerFeatures.ts pattern): no React, no i18n. The
 * label is a key — the caller runs `commissionTierLabelKey(tier)` through t().
 */

// Normalised commission tiers: <17% low, 17-20% medium, >20% high, null = unknown
export type CommissionTier = 'low' | 'medium' | 'high' | 'unknown';
export function getCommissionTier(fee: number | null): CommissionTier {
  if (fee === null) return 'unknown';
  if (fee < 17) return 'low';
  if (fee <= 20) return 'medium';
  return 'high';
}

const LABEL_KEYS: Record<CommissionTier, string> = {
  low: 'table.low_fee',
  medium: 'table.medium_fee',
  high: 'table.high_fee',
  unknown: 'table.unknown',
};

/** The `table.*` translation key of a tier's label — both baselines carry all four. */
export const commissionTierLabelKey = (tier: CommissionTier): string => LABEL_KEYS[tier];

/** Text colour of a tier's label. Literal strings — see the header. */
export const COMMISSION_TIER_TEXT_CLASS: Record<CommissionTier, string> = {
  low: 'text-emerald-500',
  medium: 'text-amber-300',
  high: 'text-red-300',
  unknown: 'text-fg-muted',
};

/** Fill colour of a tier's bar. Literal strings — see the header. */
export const COMMISSION_TIER_BAR_CLASS: Record<CommissionTier, string> = {
  low: 'bg-emerald-500',
  medium: 'bg-amber-500',
  high: 'bg-red-400',
  unknown: 'bg-slate-600',
};
